import {
    Button,
    Flex,
    Modal,
    ModalContent,
    ModalOverlay,
    useColorMode,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Keycloak from 'keycloak-js';
import { useContext, useEffect, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    AuthenticatedComponent,
    AuthenticatedKeycloak,
    isAuthenticated,
} from '../../auth/keycloak';
import config from '../../config';
import PageDataContext from '../../context/PageDataContext';
import { usePageDataCache } from '../../hooks/usePageDataCache';
import { useSessionStorage } from '../../hooks/useSessionStorage';
import {
    createCreatorSubscription,
    getProductByType,
    getUserSubscription,
    ProductDef,
    RealProduct,
    SubscriptionDef,
    TwoFactorError,
    WrapperLicense,
} from '../../service/inventory';
import {
    checkIsFollowing,
    followUser,
    getUserFollowersCount,
    getUserPrivateProfile,
    unfollowUser,
    UserProfile,
} from '../../service/social';
import { stripeAppearenceOptions } from '../../service/stripe';
import { getQuantityStringByIntl } from '../../util/label';
import { CheckoutForm } from '../storeComponents/CheckoutForm';
import { TwoFactorPermission } from '../storeComponents/TwoFactorPermission';
import { CreatorSubscriptions } from './CreatorSubscriptions';
import { DonationUI } from './DonationUI';

// Make sure to call loadStripe outside of a component’s render to avoid
// recreating the Stripe object on every render.
const stripePromise = loadStripe(config.stripePublicKey);

interface ProfileActionsProps {
    userID: string;
    keycloak: Keycloak;
    userProfile: UserProfile | undefined;
}

export const ProfileActions: AuthenticatedComponent<ProfileActionsProps> = ({
    userID,
    keycloak,
    userProfile,
}) => {
    const { isLoadFromCache } = useContext(PageDataContext);
    const [subPackages, setPackages] = usePageDataCache<ProductDef[]>('subscriptionPackages', []);
    const [subscriptions, setSubscriptions] = usePageDataCache<{
        [producID: string]: SubscriptionDef;
    }>('creatorSubscription', {});
    const [clientSecret, setClientSecret] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductDef | undefined>();
    const [myProfile, setMyProfile] = useSessionStorage<UserProfile | undefined>(
        'myProfile',
        undefined,
    );
    const localStoragePrefix = 'ProfileActions';

    const intl = useIntl();

    const toast = useToast();

    const [isFollowed, setIsFollowed] = usePageDataCache<boolean | undefined>(
        `${localStoragePrefix}_isFollowed`,
        undefined,
    );

    const [followersCount, setFollowersCount] = usePageDataCache(
        `${localStoragePrefix}_followersCount`,
        0,
    );

    const { isOpen, onOpen, onClose } = useDisclosure();

    const [isSubOpen, setIsSubOpen] = useState<boolean>(false);
    const [isStripeOpen, setIsStripeOpen] = useState<boolean>(false);
    const [isOpen2fa, setIsOpen2fa] = useState<boolean>(false);

    const { colorMode } = useColorMode();

    async function loadFollowInfo() {
        const followersCount = await getUserFollowersCount(userID);
        const isFollowed = isAuthenticated(keycloak)
            ? await checkIsFollowing(keycloak.subject, userID, keycloak.token)
            : undefined;

        setFollowersCount(followersCount);
        setIsFollowed(isFollowed);
    }

    const handleFollow = async (keycloak: AuthenticatedKeycloak) => {
        const myUserID = keycloak.subject;
        const token = keycloak.token;
        const success = await followUser(myUserID, userID, token);

        setIsFollowed(!!success);
        if (!success) {
            toast({
                title: 'Follow Error',
                description: 'Could not follow this user. Please try again later.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } else {
            setFollowersCount((followerCount) => followerCount + 1);
        }
    };

    const handleUnfollow = async (keycloak: AuthenticatedKeycloak) => {
        const myUserID = keycloak.subject;
        const token = keycloak.token;
        const success = await unfollowUser(myUserID, userID, token);

        setIsFollowed(!!!success);
        if (!success) {
            toast({
                title: 'Unfollow Error',
                description: 'Could not unfollow this user. Please try again later.',
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } else {
            setFollowersCount((followerCount) => followerCount - 1);
        }
    };

    const getPackages = async () => {
        setPackages([]);
        try {
            const token = keycloak.token;
            const packages = await getProductByType(RealProduct, WrapperLicense, token || '');
            const currency = [];
            for (let i = 0; i < packages.length; i++) {
                for (let j = 0; j < packages[i].items.length; j++) {
                    const item = packages[i].items[j].item_def;
                    if (item && item.item_type === 'creator_sub') {
                        currency.push(packages[i]);
                        break;
                    }
                }
            }
            setPackages(currency);
            getSubscription(currency);
        } catch (err: any) {
            console.error(err);
            setPackages([]);
        }
    };

    const getSubscription = async (currency: ProductDef[]) => {
        setSubscriptions({});
        try {
            const subs: { [producID: string]: SubscriptionDef } = {};
            for (let i = 0; i < currency.length; i++) {
                for (let j = 0; j < currency[i].items.length; j++) {
                    if (currency[i].items[j].item_def?.is_subscription) {
                        const def = currency[i].items[j].item_def;
                        if (def) {
                            const userSubs = await getUserSubscription(
                                keycloak.subject,
                                userID,
                                def.item_id,
                                keycloak.token,
                            );
                            if (userSubs.length > 0) {
                                subs[currency[i].product_id] = userSubs[0];
                            }
                        }
                    }
                }
            }
            setSubscriptions(subs);
        } catch (err: any) {
            console.error(err);
            setSubscriptions({});
        }
    };

    useEffect(() => {
        if (!isLoadFromCache) {
            loadFollowInfo();
            getPackages();

            async function getProfileInfo() {
                const profile = await getUserPrivateProfile(keycloak.subject, keycloak.token);
                setMyProfile(profile);
            }
            if (myProfile === undefined) {
                getProfileInfo();
            }
        }
    }, []);

    const onSubClick = async (product: ProductDef) => {
        try {
            const intent = await createCreatorSubscription(
                product.product_id,
                userID,
                keycloak.token || '',
            );
            setClientSecret(intent.client_secret);
            setSelectedProduct(product);
            setIsStripeOpen(true);
        } catch (err: any) {
            console.error('Unable to complete purchase: ', err.message);
            if (err.message === TwoFactorError) {
                setIsOpen2fa(true);
            }
        }
    };

    const appearance = stripeAppearenceOptions(colorMode);

    const elementOptions = {
        clientSecret,
        appearance,
    };

    const renderModal = () => (
        <Modal
            isOpen={isStripeOpen}
            onClose={() => {
                setIsStripeOpen(false);
                setIsSubOpen(true);
            }}
            isCentered
            scrollBehavior={'inside'}
        >
            <ModalOverlay className="modal-overlay" />
            <ModalContent className="modal-content">
                {clientSecret && (
                    <Elements options={elementOptions} stripe={stripePromise}>
                        <CheckoutForm
                            clientSecret={clientSecret}
                            keycloak={keycloak}
                            product={selectedProduct}
                            email={myProfile?.email || ''}
                            onFormClose={(err) => {
                                setIsStripeOpen(false);
                                getPackages();
                            }}
                        />
                    </Elements>
                )}
            </ModalContent>
        </Modal>
    );

    return (
        <>
            {renderModal()}
            <Flex className={`profile-actions-container ${isBrowser ? 'browser' : ''}`}>
                <Flex className={'follow-numbers'}>
                    <FormattedMessage
                        id="private_profile.follower_count"
                        defaultMessage={'{count} Followers'}
                        values={{
                            count: <b>{getQuantityStringByIntl(followersCount, intl)}</b>,
                        }}
                    />
                </Flex>
                {userID !== keycloak.subject && (
                    <>
                        <Flex
                            className={`action-buttons ${
                                !isAuthenticated(keycloak) ? 'unauthed' : ''
                            }`}
                        >
                            <Button
                                className={'follow-button'}
                                onClick={() => {
                                    if (isAuthenticated(keycloak)) {
                                        !isFollowed
                                            ? handleFollow(keycloak)
                                            : handleUnfollow(keycloak);
                                    }
                                }}
                            >
                                {!isFollowed ? 'Follow' : 'Unfollow'}
                            </Button>
                            {userProfile && userProfile.has_stripe_connect_account && (
                                <Button
                                    className={'subscribe-button'}
                                    onClick={() => {
                                        setIsSubOpen(true);
                                    }}
                                >
                                    Subscribe
                                </Button>
                            )}
                            {/* TODO: When closing and reopening should it remember state or start from the beginging? */}
                            <Button
                                isDisabled={keycloak.hasResourceRole(
                                    'disable_transfer_send',
                                    'realm',
                                )}
                                onClick={() => {
                                    if (isAuthenticated(keycloak)) {
                                        isOpen ? isBrowser && onClose() : onOpen();
                                    }
                                }}
                                className={`donate-button ${isOpen && isBrowser ? 'open' : ''}`}
                            >
                                Donate
                            </Button>
                        </Flex>
                        {isAuthenticated(keycloak) && (
                            <DonationUI
                                donateUserID={userID}
                                isOpen={isOpen}
                                onClose={onClose}
                                keycloak={keycloak}
                            />
                        )}
                    </>
                )}
                {isSubOpen && (
                    <Flex className={'subscription-box'}>
                        <CreatorSubscriptions
                            products={subPackages}
                            inventorySubscriptions={subscriptions}
                            onPackageClick={onSubClick}
                            isOpen={isSubOpen}
                            onClose={() => {
                                setIsSubOpen(false);
                            }}
                        />
                    </Flex>
                )}
            </Flex>
            <TwoFactorPermission
                isOpen={isOpen2fa}
                onClose={() => {
                    setIsOpen2fa(false);
                }}
            />
        </>
    );
};
